import { Component, OnInit, OnDestroy, AfterViewInit, ViewChild, Input } from '@angular/core'
import { ActivatedRoute, Params } from '@angular/router'
import { DownloadDocumentRendererComponent } from './download-document.renderer.component'
import { environment } from '@exchange/environment'
import { ToastrService } from 'ngx-toastr'
import { catchError } from 'rxjs/operators'
import { PayerDocumentService } from '@exchange/app/services/payer-document.service'
import { IPayerMedia, PayerMediaODataService } from '@exchange/app/domain/payerMedia'
import { FileItem } from 'ng2-file-upload'
import {
  ActionButtonsColumnRendererComponent,
  ConfirmModalComponent,
  GridSize,
  GridWrapperComponent
} from 'common-components'
import { BsModalService } from 'ngx-bootstrap/modal'
import { throwError } from 'rxjs'

@Component({
  selector: 'app-payer-contract-and-support',
  templateUrl: './payer-contract-and-support.component.html',
  styleUrls: ['./payer-contract-and-support.component.scss'],
  standalone: false
})
export class PayerContractAndSupportComponent implements OnInit, AfterViewInit, OnDestroy {
  @ViewChild('grid', { static: true })
  grid: GridWrapperComponent

  private payerId = 0
  refreshTimeout = null
  model: IPayerMedia = null
  url: string
  allowedMimeType = ['application/msword', 'application/vnd.ms-excel', 'application/pdf']
  allowedFileType = ['doc', 'xls', 'pdf']

  canDeleteDocs = true
  gridSize: GridSize = GridSize.small

  constructor(
    private route: ActivatedRoute,
    private odata: PayerMediaODataService,
    private toastr: ToastrService,
    private payerDocumentService: PayerDocumentService,
    private modalService: BsModalService
  ) {
    this.route.params.subscribe((params: Params) => {
      const id = +params['id']
      if (id) {
        this.payerId = id
      }
      this.url = `${environment.blobapiroot}/storage/payer-media/${id}`
    })
  }

  ngOnInit() {
    this.grid.defaultOrder = [{ colId: 'Filename', sort: 'asc' }]
    this.grid.gridSize = GridSize.small
    this.grid.gridOptions.context = {
      canEdit: false,
      canDelete: true,
      deleteHandler: this.deleteClicked.bind(this),
      downloadHandler: this.downloadDocument.bind(this)
    }
    this.grid.gridOptions.columnDefs = [
      {
        headerName: '',
        field: 'Id',
        width: 120,
        cellRenderer: ActionButtonsColumnRendererComponent
      },
      {
        headerName: 'Document Name',
        field: 'Filename',
        width: 350,
        cellRenderer: DownloadDocumentRendererComponent,
        filter: 'agTextColumnFilter',
        filterParams: { apply: true, newRowsAction: 'keep' }
      },
      {
        headerName: 'Last Updated',
        field: 'CreatedOnDateTime',
        cellRendererParams: {
          altValue: 'LastModifiedOnDateTime'
        },
        width: 150,
        filter: 'agDateColumnFilter',
        cellRenderer: GridWrapperComponent.DateRenderer('M/DD/YY h:mm a'),
        filterParams: { apply: true, newRowsAction: 'keep' }
      }
    ]
    this.grid.defaultFilter = `PayerId eq ${this.payerId} and IsDeleted eq false`
    this.grid.odata = this.odata
  }

  ngAfterViewInit() {
    // A proper permission doesn't exist for deleting docs so we use this
    // so that the Administrator, Clinical Leader and Operations Leader can delete them
    this.grid.api.setColumnsVisible(['Id'], this.canDeleteDocs)
  }

  ngOnDestroy() {
    if (this.refreshTimeout) {
      clearTimeout(this.refreshTimeout)
    }
  }

  downloadDocument(data: any): void {
    const media = data as IPayerMedia

    const fileToDownload = !!media.FileExtensionId ? media.SystemFileName : media.Filename
    this.payerDocumentService
      .fetchDocument(`${media.PayerId}`, fileToDownload)
      .pipe(
        catchError((error: any) => {
          return throwError(() => error)
        })
      )
      .subscribe((blob: Blob) => {
        const url = URL.createObjectURL(blob)

        const a = document.createElement('a')
        a.href = url
        a.download = media.Filename
        document.body.appendChild(a)
        a.click()

        URL.revokeObjectURL(url)
        this.showSuccess('Download successful')
      })
  }

  deleteClicked(data: any): void {
    this.model = data as IPayerMedia
    this.modalService
      .show(ConfirmModalComponent, {
        initialState: {
          title: `Delete Document?`,
          message: `Are you sure you want to delete the selected Document?`
        }
      })
      .content.onClose.subscribe(userConfirm => userConfirm && this.deleteModel())
  }

  deleteModel(): void {
    this.odata
      .delete(this.model.Id)
      .exec()
      .subscribe(() => {
        this.grid.getData()
        this.showSuccess('Deleted Successfully')
      })
  }

  showSuccess(msg?: string): void {
    if (msg) {
      this.toastr.success(msg)
    }
    const me = this
    this.refreshTimeout = setTimeout(() => {
      if (me && me.grid) {
        me.grid.getData()
      }
    }, 10000)
  }

  showError(response: any): void {
    const msg = response.toString()

    this.toastr.error(msg)
  }

  onFileUploaded(event: any): void {
    this.grid.getData()
  }

  uploadWithGuidFileName(event: FileItem): void {
    this.odata
      .post({
        Id: 0,
        PayerId: this.payerId,
        Filename: event.file.name
      } as IPayerMedia)
      .exec()
      .subscribe(
        payerMedia => {
          event.file.name = payerMedia.SystemFileName
          event.upload()
          // need to reset name so it doesn't show the guid in the ui
          event.file.name = payerMedia.Filename
        },
        (error: any) => {
          this.toastr.error(error.error.error.message)
        }
      )
  }
}
